<template>
  <div :class="{ 'calendar': true, 'trip-details-is-open': tripDetailsIsOpen }">
    <calendar-sidebar
      :aircrafts="filteredAircrafts"
      :aircraftsLoaded="aircraftsLoaded"
      :aircraftsOrdering="aircraftsOrdering"
      :flights-loaded="flightsLoaded"
      :selected-aircraft="selectedAircraft"
      :notes="notes"
      :notes-loaded="notesLoaded"
      :notes-visible="notesVisible"
      :warnings="filteredWarnings"
      :warnings-loaded="warningsLoaded"
      :warnings-visible="warningsVisible" />
    <calendar-navigation
      :date-from="dateFrom"
      :date-to="dateTo"
      :zoom="zoom"
      :zoomLevels="$options.zoomLevels"
      :currentDate="currentDate"
      :alwaysShowIcao="alwaysShowIcao"
      :notes="notes"
      :notes-loaded="notesLoaded"
      :notes-visible="notesVisible" />
    <calendar-content
      :aircrafts="filteredAircrafts"
      :aircrafts-loaded="aircraftsLoaded"
      :date-from="dateFrom"
      :date-to="dateTo"
      :flights="flights"
      :flights-loaded="flightsLoaded"
      :selectedAircraft="selectedAircraft"
      :notes="notes"
      :notes-loaded="notesLoaded"
      :notes-visible="notesVisible"
      :warnings="filteredWarnings"
      :warnings-loaded="warningsLoaded"
      :warnings-visible="warningsVisible"
      :alwaysShowIcao="alwaysShowIcao"
      :zoom="zoom" />
    <calendar-flight-details
      :aircrafts="aircrafts" />
    <calendar-trip-details
      :airports="$root.airports"
      :handlers="handlers" />
    <calendar-note-popup />
    <calendar-aircraft-note-popup />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import CalendarAircraftNotePopup from './CalendarAircraftNotePopup.vue';
import CalendarContent from './CalendarContent.vue';
import CalendarFlightDetails from './CalendarFlightDetails.vue';
import CalendarNavigation from './CalendarNavigation.vue';
import CalendarNotePopup from './CalendarNotePopup.vue';
import CalendarSidebar from './CalendarSidebar.vue';
import CalendarTripDetails from './CalendarTripDetails.vue';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'calendar',
  zoomLevels: {
    1: '1 day',
    2: '2 days',
    4: '4 days',
    7: '1 week',
    14: '2 weeks',
    30: '1 month',
    60: '2 months'
  },
  components: {
    'calendar-aircraft-note-popup': CalendarAircraftNotePopup,
    'calendar-content': CalendarContent,
    'calendar-flight-details': CalendarFlightDetails,
    'calendar-navigation': CalendarNavigation,
    'calendar-note-popup': CalendarNotePopup,
    'calendar-sidebar': CalendarSidebar,
    'calendar-trip-details': CalendarTripDetails
  },
  computed: {
    dateFrom () {
      let date = new Date();
      date.setTime(this.currentDate);

      if (this.zoom === '30' || this.zoom === '60') {
        date.setDate(1);
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') + ' 00:00:00';
    },
    dateTo () {
      let date = new Date();
      date.setTime(this.currentDate);

      if (this.zoom === '30' || this.zoom === '60') {
        date.setDate(1);
      }

      if (parseInt(this.zoom, 10) < 30) {
        date.setDate(date.getDate() + parseInt(this.zoom, 10));
      } else if (this.zoom === '30') {
        date.setMonth(date.getMonth() + 1);
      } else if (this.zoom === '60') {
        date.setMonth(date.getMonth() + 2);
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') + ' 00:00:00';
    },
    dataContextTrigger () {
      return [this.zoom, this.currentDate].join('-');
    },
    localStorageTrigger () {
      return [this.zoom, this.currentDate, this.aircraftsOrdering, this.alwaysShowIcao, this.notesVisible].join('-');
    },
    visibleAirports () {
      return [...new Set(this.flights.map(flight => ([flight.departure_airport_id, flight.arrival_airport_id])).flat())];
    },
    filteredWarnings () {
      let highWarnings = [];

      if (this.warnings['HIGH']) {
        highWarnings = this.warnings['HIGH'].slice().map(group => group.filter(warning => warning.reference_type !== 'AIRPORT' || this.visibleAirports.indexOf(warning.reference_id) > -1));
        highWarnings = highWarnings.filter(group => group.length);
      }

      let mediumWarnings = [];

      if (this.warnings['MEDIUM']) {
        mediumWarnings = this.warnings['MEDIUM'].slice().map(group => group.filter(warning => warning.reference_type !== 'AIRPORT' || this.visibleAirports.indexOf(warning.reference_id) > -1));
        mediumWarnings = mediumWarnings.filter(group => group.length);
      }

      return {
        'MEDIUM': mediumWarnings,
        'HIGH': highWarnings
      };
    },
    filteredAircrafts () {
      if (!this.aircrafts) {
        return [];
      }

      return this.aircrafts.filter(aircraft => !aircraft.is_archive);
    }
  },
  watch: {
    aircraftsOrdering () {
      this.loadAircrafts();
    },
    dataContextTrigger () {
      this.loadFlights();
      this.loadWarnings();
      this.loadNotes();
    },
    flights () {
      this.setAircraftsOrderByFlights();
    },
    localStorageTrigger () {
      this.debouncedConfigSave();
    }
  },
  data () {
    return {
      // calendar state
      zoom: '1',
      currentDate: +new Date(),
      aircraftsOrdering: 'alphabetical',
      selectedAircraft: null,
      alwaysShowIcao: false,
      // data
      aircrafts: [],
      flights: [],
      handlers: [],
      notes: [],
      warnings: {
        'HIGH': [],
        'MEDIUM': [],
        'LOW': []
      },
      // flags
      aircraftsLoaded: false,
      flightsLoaded: false,
      flightsLoadError: false,
      handlersLoaded: false,
      handlersLoadError: false,
      loadError: false,
      notesLoaded: false,
      notesLoadError: false,
      notesVisible: false,
      warningsLoaded: false,
      warningsLoadError: false,
      warningsVisible: false,
      tripDetailsIsOpen: false,
      // request markers
      aircraftsLastRequestID: 0,
      flightsLastRequestID: 0,
      notesLastRequestID: 0,
      warningsLastRequestID: 0
    };
  },
  beforeMount () {
    if (localStorage.getItem('crm-config-current-date')) {
      this.currentDate = parseInt(localStorage.getItem('crm-config-current-date'), 10);
    }

    if (localStorage.getItem('crm-config-always-show-icao')) {
      this.alwaysShowIcao = localStorage.getItem('crm-config-always-show-icao') === 'true';
    }

    if (localStorage.getItem('crm-config-show-notes')) {
      this.notesVisible = localStorage.getItem('crm-config-show-notes') === 'true';
    }

    if (localStorage.getItem('crm-config-aircrafts-ordering')) {
      let newValue = localStorage.getItem('crm-config-aircrafts-ordering');

      if (['live', 'alphabetical', 'custom'].indexOf(newValue) > -1) {
        this.aircraftsOrdering = newValue;
      }
    }

    if (localStorage.getItem('crm-config-zoom')) {
      let newValue = localStorage.getItem('crm-config-zoom');

      if (['1', '2', '4', '7', '14', '30', '60'].indexOf(newValue) > -1) {
        this.zoom = newValue;
      }
    }
  },
  mounted () {
    this.loadAircrafts();
    this.loadFlights();
    this.loadHandlers();
    this.loadWarnings();
    this.loadNotes();

    this.$bus.$on('calendar-zoom-in', this.zoomIn);
    this.$bus.$on('calendar-zoom-out', this.zoomOut);
    this.$bus.$on('calendar-set-date', this.setCurrentDate);
    this.$bus.$on('calendar-update-aircrafts-list', this.loadAircrafts);
    this.$bus.$on('calendar-toggle-warnings-visibility', this.setWarningsVisibility);
    this.$bus.$on('calendar-toggle-notes-visibility', this.setNotesVisibility);
    this.$bus.$on('calendar-aircrafts-update', this.loadAircrafts);
    this.$bus.$on('calendar-flights-update', this.loadFlights);
    this.$bus.$on('calendar-notes-update', this.loadNotes);
    this.$bus.$on('calendar-change-aircrafts-ordering', this.setAircraftsOrdering);
    this.$bus.$on('calendar-toggle-selected-aircraft', this.setSelectedAircraft);
    this.$bus.$on('calendar-trip-details-pane-show', this.openTripDetails);
    this.$bus.$on('calendar-trip-details-pane-close', this.closeTripDetails);
    this.$bus.$on('calendar-reload-handlers', this.loadHandlers);
    this.$bus.$on('calendar-change-aircrafts-custom-ordering', this.saveAircraftsCustomOrdering);
    this.$bus.$on('calendar-always-show-icao-change', this.changeIcaoVisibility);
  },
  methods: {
    zoomIn () {
      let levels = Object.keys(this.$options.zoomLevels);
      let index = levels.indexOf(this.zoom);

      if (index <= 0) {
        return;
      }

      this.zoom = levels[index - 1];
    },
    zoomOut () {
      let levels = Object.keys(this.$options.zoomLevels);
      let index = levels.indexOf(this.zoom);

      if (index >= levels.length - 1) {
        return;
      }

      this.zoom = levels[index + 1];
    },
    setCurrentDate (newDate) {
      this.currentDate = newDate;
    },
    loadAircrafts () {
      let self = this;
      this.aircraftsLoaded = false;
      this.aircraftsLastRequestID = +new Date();

      FormUtils.loadAdditionalData(this, {
        endpoint: '/calendar/aircrafts',
        outputKey: 'aircrafts',
        loadedKey: 'aircraftsLoaded',
        errorKey: 'loadError',
        lastRequestID: this.aircraftsLastRequestID,
        lastRequestIdKey: 'aircraftsLastRequestID',
        params: {
          pagination: 0,
          ordering: this.aircraftsOrdering
        },
        successAction () {
          if (self.aircraftsOrdering === 'live') {
            self.setAircraftsOrderByFlights();
          }

          self.$bus.$emit('calendar-sidebar-update-aircrafts-clone');
        }
      });
    },
    loadFlights () {
      this.flightsLoaded = false;
      this.flightsLastRequestID = +new Date();

      FormUtils.loadAdditionalData(this, {
        endpoint: '/calendar/flights',
        outputKey: 'flights',
        loadedKey: 'flightsLoaded',
        errorKey: 'flightsLoadError',
        lastRequestID: this.flightsLastRequestID,
        lastRequestIdKey: 'flightsLastRequestID',
        params: {
          where: {
            date_from: this.dateFrom,
            date_to: this.dateTo
          }
        },
        successAction: () => {
          this.flightsLoaded = true;
          this.$bus.$emit('calendar-flights-updated');
        }
      });
    },
    loadHandlers () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/handlers/items?simple=1',
        params: {
          pagination: 0
        },
        outputKey: 'handlers',
        loadedKey: 'handlersLoaded',
        errorKey: 'handlersLoadError'
      });
    },
    loadWarnings () {
      this.warningsLoaded = false;
      this.warningsLastRequestID = +new Date();

      FormUtils.loadAdditionalData(this, {
        endpoint: '/calendar/warnings',
        outputKey: 'warnings',
        loadedKey: 'warningsLoaded',
        errorKey: 'warningsLoadError',
        lastRequestID: this.warningsLastRequestID,
        lastRequestIdKey: 'warningsLastRequestID',
        params: {
          where: {
            date_from: this.dateFrom,
            date_to: this.dateTo
          }
        },
        successAction () {
          this.warningsLoaded = true;
        }
      });
    },
    setWarningsVisibility (visibility) {
      this.warningsVisible = visibility;
    },
    loadNotes () {
      this.notesLoaded = false;
      this.notesLastRequestID = +new Date();

      FormUtils.loadAdditionalData(this, {
        endpoint: '/notes/items',
        outputKey: 'notes',
        loadedKey: 'notesLoaded',
        errorKey: 'notesLoadError',
        lastRequestID: this.notesLastRequestID,
        lastRequestIdKey: 'notesLastRequestID',
        params: {
          where: {
            date_from: this.dateFrom.split(' ')[0],
            date_to: this.zoom === '1' ? this.dateFrom.split(' ')[0] : this.dateTo.split(' ')[0]
          }
        },
        successAction () {
          this.notesLoaded = true;
        }
      });
    },
    setNotesVisibility (visibility) {
      this.notesVisible = visibility;
    },
    setAircraftsOrdering (ordering) {
      this.aircraftsOrdering = ordering;
    },
    setAircraftsOrderByFlights () {
      if (!this.aircrafts.length || this.aircraftsOrdering === 'alphabetical' || this.aircraftsOrdering === 'custom') {
        return;
      }

      for (let i = 0; i < this.aircrafts.length; i++) {
        this.aircrafts[i].visible_flights = 0;
      }

      if (!this.flights.length) {
        return;
      }

      for (let i = 0; i < this.flights.length; i++) {
        if (this.flights[i].aircraft_id) {
          let index = this.aircrafts.findIndex(aircraft => aircraft.id === this.flights[i].aircraft_id);

          if (index > -1) {
            this.aircrafts[index].visible_flights++;
          }
        }
      }

      // left aircrafts with visible_flights > 0 untouched and move to the end aircrafts with visible_flights = 0
      this.aircrafts.sort((aircraftA, aircraftB) => {
        if (aircraftA.visible_flights === 0 && aircraftB.visible_flights === 0) {
          return 0;
        }

        if (aircraftB.visible_flights === 0) {
          return -1;
        }

        return 0;
      });
    },
    configSave () {
      localStorage.setItem('crm-config-current-date', this.currentDate);
      localStorage.setItem('crm-config-aircrafts-ordering', this.aircraftsOrdering);
      localStorage.setItem('crm-config-zoom', this.zoom);
      localStorage.setItem('crm-config-always-show-icao', this.alwaysShowIcao);
      localStorage.setItem('crm-config-show-notes', this.notesVisible);
    },
    // eslint-disable-next-line
    debouncedConfigSave: debounce(function () {
      this.configSave();
    }, 1000),
    setSelectedAircraft (aircraftID) {
      if (this.selectedAircraft === aircraftID) {
        this.selectedAircraft = null;
        return;
      }

      this.selectedAircraft = aircraftID;
    },
    openTripDetails () {
      this.tripDetailsIsOpen = true;
    },
    closeTripDetails () {
      this.tripDetailsIsOpen = false;
    },
    saveAircraftsCustomOrdering (aircraftsClone) {
      this.aircrafts = aircraftsClone.slice();

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          items: this.aircrafts.map(aircraft => aircraft.id)
        },
        endpoint: '/calendar/aircrafts/store-ordering',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during saving the custom ordering. Please try again',
        successAction: () => false
      });
    },
    changeIcaoVisibility (newValue) {
      this.alwaysShowIcao = !!newValue;
    }
  },
  beforeDestroy () {
    this.$bus.$off('calendar-zoom-in', this.zoomIn);
    this.$bus.$off('calendar-zoom-out', this.zoomOut);
    this.$bus.$off('calendar-set-date', this.setCurrentDate);
    this.$bus.$off('calendar-update-aircrafts-list', this.loadAircrafts);
    this.$bus.$off('calendar-toggle-warnings-visibility', this.setWarningsVisibility);
    this.$bus.$off('calendar-toggle-notes-visibility', this.setNotesVisibility);
    this.$bus.$off('calendar-aircrafts-update', this.loadAircrafts);
    this.$bus.$off('calendar-flights-update', this.loadFlights);
    this.$bus.$off('calendar-warnings-update', this.loadWarnings);
    this.$bus.$off('calendar-notes-update', this.loadNotes);
    this.$bus.$off('calendar-change-aircrafts-ordering', this.setAircraftsOrdering);
    this.$bus.$off('calendar-toggle-selected-aircraft', this.setSelectedAircraft);
    this.$bus.$off('calendar-trip-details-pane-show', this.openTripDetails);
    this.$bus.$off('calendar-trip-details-pane-close', this.closeTripDetails);
    this.$bus.$off('calendar-reload-handlers', this.loadHandlers);
    this.$bus.$off('calendar-change-aircrafts-custom-ordering', this.saveAircraftsCustomOrdering);
    this.$bus.$off('calendar-always-show-icao-change', this.changeIcaoVisibility);
  }
};
</script>

<style lang="scss" scoped>
.calendar {
  display: flex;

  .calendar-sidebar,
  .calendar-content {
    padding-bottom: 250px;
  }

  &.trip-details-is-open {
    .calendar-sidebar,
    .calendar-content {
      padding-bottom: 450px;
    }
  }
}
</style>
